import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState } from "react";

export function Sorry() {
  const player = usePlayer();
  const game = useGame();
  const [showReturnMessage, setShowReturnMessage] = useState(false);

  // Use .get() for custom properties set via player.set()
  const exitStatus = player?.get("exitStatus");
  const exitReason = player?.get("exitReason");

  console.log("[Sorry] exitStatus:", exitStatus, "exitReason:", exitReason);

  // Handle "Begin Again" - create new player identity and reload
  const handleBeginAgain = () => {
    // For Prolific users (PROLIFIC_PID in URL), we need to modify the URL
    // to create a new player identity while preserving their Prolific ID
    const urlParams = new URLSearchParams(window.location.search);
    const prolificPid = urlParams.get("PROLIFIC_PID");

    if (prolificPid) {
      // Append retry suffix to create a new player identity
      // e.g., "ABC123" becomes "ABC123_retry_1" or "ABC123_retry_2"
      const currentRetry = parseInt(urlParams.get("retry") || "0", 10);
      urlParams.set("retry", String(currentRetry + 1));

      // Update URL and reload
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.location.href = newUrl;
    } else {
      // For non-Prolific users, clear session storage and reload
      sessionStorage.removeItem("empirica_player_key");
      window.location.reload();
    }
  };

  // Special handling for partner dropout - show two options
  if (exitStatus === "partnerDropout" && !showReturnMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8 max-w-lg">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Partner Disconnected</h1>
          <p className="text-lg text-gray-600 mb-6">
            Unfortunately, your partner left the study or lost connection, so the game had to end early.
          </p>
          <p className="text-md text-gray-700 mb-8">
            We're sorry for the inconvenience. You have two options:
          </p>

          <div className="space-y-4">
            {/* Option 1: Begin Again */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">Option 1: Try Again</h3>
              <p className="text-sm text-gray-600 mb-3">
                Start fresh and wait to be matched with a new partner. You will need to redo the initial steps (consent, instructions, and domain ranking).
              </p>
              <button
                onClick={handleBeginAgain}
                className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Begin Again
              </button>
            </div>

            {/* Option 2: Return Submission */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Option 2: Return Submission</h3>
              <p className="text-sm text-gray-600 mb-3">
                Return your submission on Prolific if you prefer not to continue.
              </p>
              <button
                onClick={() => setShowReturnMessage(true)}
                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return Submission
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show return submission message for partner dropout
  if (exitStatus === "partnerDropout" && showReturnMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8 max-w-lg">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">We're Sorry</h1>
          <p className="text-lg text-gray-600 mb-6">
            We apologize for the inconvenience caused by your partner disconnecting.
          </p>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
            <p className="text-md text-gray-700 mb-3">
              Please return your submission on Prolific so you can participate in another study without losing time.
            </p>
            <p className="text-sm text-gray-500">
              If there's something you think we should consider, please send us a message on Prolific.
            </p>
          </div>

          <button
            onClick={() => setShowReturnMessage(false)}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Go back to options
          </button>
        </div>
      </div>
    );
  }

  // Default handling for other exit statuses
  let msg;
  let prolificCode = null;

  switch (exitStatus) {
    case "gameFull":
      msg = "All games you are eligible for have filled up too fast...";
      break;
    case "gameLobbyTimedOut":
      msg = "We waited 10 minutes but no partner joined. The study could not start.";
      prolificCode = "NOPARTNER";
      break;
    case "playerEndedLobbyWait":
      msg = "You decided to stop waiting, we are sorry it was too long a wait.";
      break;
    case "dropout":
      msg = "The session ended because a participant became inactive or disconnected.";
      break;
    case "batchClosed":
      msg = "The study session was closed by the researcher. Thank you for your time.";
      break;
    default:
      msg = "The session ended unexpectedly. Please contact the researcher.";
      break;
  }

  if (exitReason === "failedQuestion") {
    msg = "Unfortunately you did not meet the conditions required to play the game.";
    prolificCode = null;
  }

  // Only for dev
  if (!game && import.meta.env.DEV) {
    msg = "Unfortunately the Game was cancelled because of failed to init Game (only visible in development, check the logs).";
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Sorry!</h1>
        <p className="text-lg text-gray-600 mb-4">
          Sorry, you were not able to play today! {msg}
        </p>

        {/* Show Prolific code for compensation */}
        {prolificCode && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 mb-2">
              Please return to Prolific and enter the following code:
            </p>
            <p className="text-2xl font-mono font-bold text-blue-900 bg-white px-4 py-3 rounded border">
              {prolificCode}
            </p>
          </div>
        )}

        {!prolificCode && (
          <p className="text-md font-semibold text-gray-700">
            Please contact the researcher to see if there are more games available.
          </p>
        )}
      </div>
    </div>
  );
}
